/**
 * Audit Lite API (`<API base>/supplier-audits/`). Every call 404s while the
 * tenant has Audit Lite switched off.
 */
import api, { fetchAllPages } from './client';
import type {
  Audit, AuditAction, AuditActionInput, AuditActionStatus, AuditAnswerInput, AuditDetail, AuditDetailsInput,
  AuditNote, AuditOutcome, AuditPhoto, AuditStartInput, AuditSummary, AuditTemplate,
} from './types/audits';

// Relative to the client's base URL, which is `/api` locally and `/api/v1` on
// the deployed apps (the backend serves both). Never hard-code `/v1` here:
// `/v1/supplier-audits` became `/api/v1/v1/supplier-audits` live (9 Oct 2026).
const BASE = '/supplier-audits';

/** A list endpoint answers either a page (`results`) or a bare array. */
function rowsOf<T>(data: { results?: T[] } | T[]): T[] {
  return Array.isArray(data) ? data : (data.results ?? []);
}

/** Every row of a paginated list (filters and counts run client-side, so page 1 is not enough). */
function allRows<T>(url: string, params: Record<string, string | number> = {}): Promise<T[]> {
  return fetchAllPages<T>((page) => api.get(url, { params: { ...params, page, page_size: 100 } }));
}

export interface AuditListParams {
  supplier?: number;
  status?: string;
  auditor?: 'me' | number;
  /** The audit behind a questionnaire id (old questionnaire links). */
  qualification?: number;
  /** true = only archived audits (sent as `archived=1`); otherwise the server leaves them out. */
  archived?: boolean;
}

export interface AuditActionListParams {
  status?: string;
  owner?: 'me' | number;
  supplier?: number;
  audit?: number;
  overdue?: boolean;
}

export const auditsAPI = {
  list: async (params: AuditListParams = {}): Promise<Audit[]> => {
    const { archived, ...rest } = params;
    const query: Record<string, string | number> = {};
    Object.entries(rest).forEach(([key, value]) => { if (value !== undefined) query[key] = value; });
    if (archived) query['archived'] = 1;
    return allRows<Audit>(`${BASE}/audits/`, query);
  },
  get: async (id: number): Promise<AuditDetail> => (await api.get(`${BASE}/audits/${id}/`)).data,
  /** Continues an audit of that supplier + template already in progress, unless `new` is true. */
  start: async (payload: AuditStartInput): Promise<AuditDetail> =>
    (await api.post(`${BASE}/audits/start/`, payload)).data,
  summary: async (supplier: number): Promise<AuditSummary> =>
    (await api.get(`${BASE}/audits/summary/`, { params: { supplier } })).data,
  saveAnswers: async (id: number, responses: AuditAnswerInput[]): Promise<{ saved: number }> =>
    (await api.post(`${BASE}/audits/${id}/save-answers/`, { responses })).data,
  uploadFile: async (id: number, question: number, file: File): Promise<{ file_path: string; file_name: string }> => {
    const form = new FormData();
    form.append('question', String(question));
    form.append('file', file);
    return (await api.post(`${BASE}/audits/${id}/upload-file/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  downloadFile: async (id: number, question: number): Promise<Blob> =>
    (await api.get(`${BASE}/audits/${id}/download-file/`, { params: { question }, responseType: 'blob' })).data,
  /** Clears the file answer of a file_upload check. */
  removeFile: async (id: number, question: number): Promise<void> => {
    await api.post(`${BASE}/audits/${id}/remove-file/`, { question });
  },
  /** One photo (or file) on a check; a check takes several. */
  uploadPhoto: async (id: number, question: number, file: File): Promise<AuditPhoto> => {
    const form = new FormData();
    form.append('question', String(question));
    form.append('file', file);
    return (await api.post(`${BASE}/audits/${id}/photos/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  deletePhoto: async (id: number, photoId: number): Promise<void> => {
    await api.delete(`${BASE}/audits/${id}/photos/${photoId}/`);
  },
  downloadPhoto: async (id: number, photoId: number): Promise<Blob> =>
    (await api.get(`${BASE}/audits/${id}/photos/${photoId}/download/`, { responseType: 'blob' })).data,
  complete: async (id: number, outcome: AuditOutcome, summary: string): Promise<AuditDetail> =>
    (await api.post(`${BASE}/audits/${id}/complete/`, { outcome, summary })).data,
  updateDetails: async (id: number, payload: AuditDetailsInput): Promise<AuditDetail> =>
    (await api.patch(`${BASE}/audits/${id}/details/`, payload)).data,
  /** The completed audit as a PDF report; `filename` from the server. */
  exportPdf: async (id: number): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`${BASE}/audits/${id}/export/`, { responseType: 'blob' });
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const match = /filename="?([^";]+)"?/.exec(disposition);
    return { blob: response.data as Blob, filename: match?.[1] ?? `Audit_${id}.pdf` };
  },
  reopen: async (id: number, reason: string): Promise<AuditDetail> =>
    (await api.post(`${BASE}/audits/${id}/reopen/`, { reason })).data,
  /** Customer admins only: hide it from lists, profile, reminders and calendar; read-only until restored. */
  archive: async (id: number): Promise<AuditDetail> =>
    (await api.post(`${BASE}/audits/${id}/archive/`)).data,
  unarchive: async (id: number): Promise<AuditDetail> =>
    (await api.post(`${BASE}/audits/${id}/unarchive/`)).data,
  /** Customer admins only, in-progress audits only (400 for a completed one — archive it instead). */
  remove: async (id: number): Promise<void> => {
    await api.delete(`${BASE}/audits/${id}/`);
  },
  notes: async (id: number): Promise<AuditNote[]> =>
    rowsOf<AuditNote>((await api.get(`${BASE}/audits/${id}/notes/`)).data),
  addNote: async (id: number, body: string): Promise<AuditNote> =>
    (await api.post(`${BASE}/audits/${id}/notes/`, { body })).data,

  templates: async (): Promise<AuditTemplate[]> => allRows<AuditTemplate>(`${BASE}/audit-templates/`),
  setTemplateAudit: async (id: number, isAudit: boolean): Promise<AuditTemplate> =>
    (await api.post(`${BASE}/audit-templates/${id}/set-audit/`, { is_audit: isAudit })).data,

  actions: async (params: AuditActionListParams = {}): Promise<AuditAction[]> => {
    const query: Record<string, string | number> = {};
    if (params.status) query['status'] = params.status;
    if (params.owner !== undefined) query['owner'] = params.owner;
    if (params.supplier !== undefined) query['supplier'] = params.supplier;
    if (params.audit !== undefined) query['audit'] = params.audit;
    if (params.overdue) query['overdue'] = 1;
    return allRows<AuditAction>(`${BASE}/actions/`, query);
  },
  raiseAction: async (payload: AuditActionInput): Promise<AuditAction> =>
    (await api.post(`${BASE}/actions/`, payload)).data,
  updateAction: async (id: number, payload: Partial<AuditActionInput>): Promise<AuditAction> =>
    (await api.patch(`${BASE}/actions/${id}/`, payload)).data,
  setActionStatus: async (id: number, status: Extract<AuditActionStatus, 'open' | 'in_progress'>): Promise<AuditAction> =>
    (await api.post(`${BASE}/actions/${id}/set-status/`, { status })).data,
  closeAction: async (id: number, status: Extract<AuditActionStatus, 'completed' | 'cancelled'>, completionNotes: string): Promise<AuditAction> =>
    (await api.post(`${BASE}/actions/${id}/close/`, { status, completion_notes: completionNotes })).data,
};
